import React from 'react';
import {
  MenuItem,
  Pagination as MuiPagination,
  PaginationItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconWrapper } from 'components/Icon';
import { PAGE_SIZE_OPTIONS } from 'hook/usePageSize';

const Next = ({ label }) => (
  <Stack direction="row" gap={1} alignItems="center">
    <Typography variant="caption" color="primary" fontWeight={700}>
      {label}
    </Typography>
    <IconWrapper icon="chevronRight" sx={{ svg: { width: 20, height: 20 } }} />
  </Stack>
);
const Previous = ({ label }) => (
  <Stack direction="row" gap={1} alignItems="center">
    <IconWrapper icon="chevronLeft" sx={{ svg: { width: 20, height: 20 } }} />
    <Typography variant="caption" color="primary">
      {label}
    </Typography>
  </Stack>
);

export const Pagination = ({
  handlePageChange,
  itemCount = 0,
  itemsPerPage = 10,
  page = 1,
  prevLabel = 'Prev',
  nextLabel = 'Next',
  onItemsPerPageChange,
  itemsPerPageLabel = 'Per page',
}) => {
  const pageCount =
    itemCount % itemsPerPage === 0
      ? Math.floor(itemCount / itemsPerPage)
      : Math.floor(itemCount / itemsPerPage) + 1;

  const handleItemsPerPageChange = (event) => {
    onItemsPerPageChange(event.target.value);
    handlePageChange(event, 1);
  };

  const pagination = (
    <MuiPagination
      sx={({ palette, shadows }) => ({
        button: { boxShadow: shadows[25], border: 'none' },
        '& > ul .MuiPaginationItem-ellipsis': {
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          pb: 0.3,
          color: palette.grey[500],
          height: '32px',
          boxShadow: shadows[25],
          borderRadius: 1,
        },
        '& > ul > li:first-of-type > button, & > ul > li:last-child > button': {
          border: 'none',
          boxShadow: 'none',
        },
        '& > ul :not(.Mui-selected) > button, & > ul button.Mui-disabled *': {
          color: `${palette.grey[500]} !important`,
        },
        '& > ul button.Mui-selected': {
          color: `${palette.primary.main} !important`,
        },
      })}
      page={page}
      count={pageCount}
      onChange={handlePageChange}
      shape="rounded"
      color="primary"
      variant="outlined"
      renderItem={(item) => (
        <PaginationItem
          slots={{
            previous: Previous,
            next: Next,
          }}
          slotProps={{
            previous: { label: prevLabel },
            next: { label: nextLabel },
          }}
          {...item}
        />
      )}
    />
  );

  if (!onItemsPerPageChange || itemCount <= PAGE_SIZE_OPTIONS[0]) return pagination;

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" flexWrap="wrap" gap={2}>
      {pagination}
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {itemsPerPageLabel}
        </Typography>
        <TextField
          select
          size="small"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          inputProps={{ 'aria-label': itemsPerPageLabel }}
          SelectProps={{
            IconComponent: () => null,
            renderValue: (value) => (
              <Stack direction="row" alignItems="center" gap={0.5}>
                {value}
                <IconWrapper icon="chevronDown" sx={{ svg: { width: 16, height: 16 } }} />
              </Stack>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': { height: 32 },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              py: '0 !important',
              pl: '12px !important',
              pr: '8px !important',
              fontSize: '0.875rem',
              fontWeight: 700,
            },
          }}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Stack>
  );
};
